class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.container = this.closest('[data-search-popup]') || document.querySelector('[data-search-popup]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.searchPopupResults = this.container?.querySelector('.m-search-popup__result');
    this.discovery = this.container?.querySelector('[data-search-discovery]');
    this.popularSearches = this.container?.querySelector('[data-popular-searches]');
    this.searchCount = this.container?.querySelector('[data-search-count]');
    this.clearButton = this.container?.querySelector('[data-clear-search]');
    this.loading = this.container?.querySelector('[data-spinner]');
    this.submit = this.querySelector('button[type="submit"]');
    this.message = this.container?.querySelector('[data-message]');
    this.queryLabel = this.container?.querySelector('[data-query]');
    this.moreResultIcon = this.container?.querySelector('[data-more-result-icon]');
    this.cache = new Map();
    this.cacheLimit = 40;
    this.abortController = null;
    this.requestId = 0;
    this.resultsMaxHeight = null;
    this.isOpen = false;
    this.activeCollection = null;

    this.setupPopularSearches();
    this.setupEventListeners();
  }

  setupPopularSearches() {
    const route = window.MinimogSettings?.routes?.search_url || '/search';
    this.container?.querySelectorAll('[data-ps-item]').forEach((item) => {
      const query = item.dataset.psQuery?.trim();
      if (!query) return;
      item.href = `${route}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
    });
  }

  setupEventListeners() {
    const form = this.querySelector('form.m-search-form');
    const debouncedChange = debounce(() => this.onChange(), 220);

    form?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input?.addEventListener('input', () => {
      this.activeCollection = null;
      debouncedChange();
    });
    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close(false);
    });
    this.clearButton?.addEventListener('click', this.onClearSearch.bind(this));

    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.closeSearchPopup) {
      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
    }
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  normalizeTerm(value) {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  compactTerm(value) {
    return this.normalizeTerm(value).replace(/\s+/g, '');
  }

  onChange() {
    const query = this.getQuery();
    if (!query) {
      this.abortController?.abort();
      this.activeCollection = null;
      this.showDiscovery();
      this.toggleSpinnerLoading(false);
      this.toggleClearSearch(false);
      return;
    }
    this.getSearchResults(query);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query) {
      event.preventDefault();
      return;
    }

    const queryKey = this.compactTerm(query);
    if (this.activeCollection?.url && this.activeCollection.queryKey === queryKey) {
      event.preventDefault();
      window.location.assign(this.activeCollection.url);
    }
  }

  onClearSearch(event) {
    event.preventDefault();
    this.abortController?.abort();
    this.activeCollection = null;
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.showDiscovery();
    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(false);
  }

  getSearchFields() {
    let fields = 'title,product_type,vendor,variants.sku,variants.title';
    if (this.dataset.searchByTag === 'true') fields += ',tag';
    if (this.dataset.searchByBody === 'true') fields += ',body';
    return fields;
  }

  setCachedResult(key, html) {
    if (this.cache.has(key)) this.cache.delete(key);
    this.cache.set(key, html);
    if (this.cache.size > this.cacheLimit) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
  }

  extractSectionHtml(text, sectionId) {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const section = doc.querySelector(`#shopify-section-${sectionId}`);
    return section?.innerHTML || '';
  }

  getCollectionCandidates(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const seen = new Set();

    return Array.from(doc.querySelectorAll('[data-search-collection]'))
      .map((link) => {
        const title = link.textContent.trim();
        const url = link.getAttribute('href') || '';
        let handle = '';

        try {
          const parsed = new URL(url, window.location.origin);
          const marker = '/collections/';
          const index = parsed.pathname.indexOf(marker);
          if (index !== -1) {
            handle = decodeURIComponent(parsed.pathname.slice(index + marker.length).split('/')[0]).replace(/-/g, ' ');
          }
        } catch (error) {
          handle = '';
        }

        return { title, url, handle };
      })
      .filter((item) => {
        if (!item.title || !item.url || !item.url.includes('/collections/')) return false;
        if (seen.has(item.url)) return false;
        seen.add(item.url);
        return true;
      });
  }

  findCollectionMatch(query, candidates) {
    const queryKey = this.compactTerm(query);
    if (queryKey.length < 2 || !candidates.length) return null;

    const items = candidates.map((item) => ({
      ...item,
      titleKey: this.compactTerm(item.title),
      handleKey: this.compactTerm(item.handle)
    }));

    const exactHandle = items.filter((item) => item.handleKey === queryKey);
    if (exactHandle.length === 1) return exactHandle[0];

    const exactTitle = items.filter((item) => item.titleKey === queryKey);
    if (exactTitle.length === 1) return exactTitle[0];

    if (queryKey.length < 3) return null;

    const prefixMatches = items.filter((item) =>
      item.titleKey.startsWith(queryKey) || item.handleKey.startsWith(queryKey)
    );

    if (prefixMatches.length === 1) return prefixMatches[0];
    return null;
  }

  getCollectionPreviewUrl(collectionUrl) {
    const url = new URL(collectionUrl, window.location.origin);
    url.searchParams.set('sort_by', 'best-selling');
    url.searchParams.set('section_id', 'tw-search-collection-preview');
    return `${url.pathname}${url.search}`;
  }

  async processPredictiveHtml(html, query, currentRequest) {
    if (currentRequest !== this.requestId) return;

    const candidates = this.getCollectionCandidates(html);
    const collectionMatch = this.findCollectionMatch(query, candidates);

    if (collectionMatch) {
      const previewCacheKey = `collection|${collectionMatch.url}|best-selling`;
      let previewHtml = this.cache.get(previewCacheKey) || '';

      if (!previewHtml) {
        try {
          const previewResponse = await fetch(this.getCollectionPreviewUrl(collectionMatch.url), {
            signal: this.abortController.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });

          if (!previewResponse.ok) throw new Error(`Collection preview failed: ${previewResponse.status}`);
          const previewText = await previewResponse.text();
          if (currentRequest !== this.requestId) return;

          const sectionHtml = this.extractSectionHtml(previewText, 'tw-search-collection-preview');
          const previewDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const wrapper = previewDoc.querySelector('[data-search-items-wrapper]');

          if (wrapper?.dataset.hasResults === 'true') {
            previewHtml = sectionHtml;
            this.setCachedResult(previewCacheKey, previewHtml);
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.warn(error);
        }
      }

      if (previewHtml && currentRequest === this.requestId) {
        this.activeCollection = {
          title: collectionMatch.title,
          url: collectionMatch.url,
          queryKey: this.compactTerm(query)
        };
        this.renderSearchResults(previewHtml, query, this.activeCollection);
        return;
      }
    }

    if (currentRequest !== this.requestId) return;
    this.activeCollection = null;
    this.renderSearchResults(html, query, null);
  }

  async getSearchResults(query) {
    const fields = this.getSearchFields();
    const unavailableProducts = this.dataset.unavailableProductsOption || 'last';
    const queryCacheKey = `search|${query.toLowerCase().replace(/\s+/g, '-')}|${fields}|${unavailableProducts}`;

    this.hideDiscovery();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    this.abortController?.abort();
    this.abortController = new AbortController();
    const currentRequest = ++this.requestId;

    try {
      let html = this.cache.get(queryCacheKey) || '';

      if (!html) {
        const route = window.MinimogSettings?.routes?.predictive_search_url || '/search/suggest';
        const url = `${route}?q=${encodeURIComponent(query)}&resources[type]=product,query,collection&resources[limit]=8&resources[options][unavailable_products]=${encodeURIComponent(unavailableProducts)}&resources[options][fields]=${encodeURIComponent(fields)}&section_id=predictive-search`;
        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const text = await response.text();
        if (currentRequest !== this.requestId) return;

        html = this.extractSectionHtml(text, 'predictive-search');
        if (!html) throw new Error('Predictive search section missing');
        this.setCachedResult(queryCacheKey, html);
      }

      await this.processPredictiveHtml(html, query, currentRequest);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.close(false);
      this.toggleSpinnerLoading(false);
      this.toggleClearSearch(true);
    }
  }

  renderSearchResults(html, query, collection = null) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = html;
    this.setAttribute('results', '');

    const wrapper = this.predictiveSearchResults.querySelector('[data-search-items-wrapper]');
    const hasResults = wrapper?.dataset.hasResults === 'true';

    this.renderSearchQueryAndMessage(hasResults, query, collection);
    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(hasResults, query, collection = null) {
    this.predictiveSearchResults?.classList.remove('m:hidden');
    this.searchPopupResults?.classList.remove('m:hidden');

    if (collection) {
      if (this.queryLabel) this.queryLabel.textContent = collection.title;
      if (this.message) this.message.textContent = 'View collection';
      this.moreResultIcon?.classList.remove('m:hidden');
      return;
    }

    if (this.queryLabel) this.queryLabel.textContent = query;
    if (this.message) {
      const resultsTitle = this.message.dataset.resultsTitle || 'Search results for';
      this.message.textContent = hasResults ? resultsTitle : 'Search for';
    }
    this.moreResultIcon?.classList.toggle('m:hidden', hasResults);
  }

  toggleSpinnerLoading(isLoading) {
    if (this.submit) this.submit.style.visibility = isLoading ? 'hidden' : 'visible';
    if (this.loading) this.loading.style.visibility = isLoading ? 'visible' : 'hidden';
  }

  toggleClearSearch(show) {
    if (this.clearButton) this.clearButton.style.visibility = show ? 'visible' : 'hidden';
  }

  showDiscovery() {
    this.discovery?.classList.remove('m:hidden');
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.predictiveSearchResults?.classList.add('m:hidden');
    this.searchPopupResults?.classList.add('m:hidden');
    this.searchCount?.classList.add('m:hidden');
    this.input?.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
  }

  hideDiscovery() {
    this.discovery?.classList.add('m:hidden');
  }

  open() {
    if (!this.searchPopupResults || !this.input) return;
    this.hideDiscovery();
    this.searchPopupResults.style.setProperty('--search-result-max-height', `${this.getResultsMaxHeight()}px`);
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.searchCount?.classList.remove('m:hidden');
  }

  close(clearInput = false) {
    if (clearInput && this.input) this.input.value = '';
    if (clearInput) {
      this.activeCollection = null;
      this.removeAttribute('results');
    }

    this.removeAttribute('open');
    this.predictiveSearchResults?.classList.add('m:hidden');
    this.searchPopupResults?.classList.add('m:hidden');
    this.searchCount?.classList.add('m:hidden');
    this.input?.setAttribute('aria-expanded', 'false');
    this.toggleClearSearch(Boolean(this.getQuery()));
    this.isOpen = false;

    if (!this.getQuery()) this.showDiscovery();
  }

  getResultsMaxHeight() {
    const searchElement = this.getBoundingClientRect();
    return Math.max(280, window.innerHeight - searchElement.bottom - 16);
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
